import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useShallow } from 'zustand/react/shallow';

import CalendarMonthV2Screen, { type CalendarMonthV2FocusRequest } from '../../src/features/calendar-month-v2/CalendarMonthV2Screen';
import QuickAddFAB from '../../src/components/calendar/QuickAddFAB';
import CoachMark from '../../src/components/onboarding/CoachMark';
import { useCalendarStore } from '../../src/stores/calendarStore';
import { useRemindersStore } from '../../src/stores/reminderStore';
import { useAuthStore } from '../../src/stores/authStore';
import { useNavigationStore } from '../../src/stores/navigationStore';
import { updateReminder } from '../../src/api/client';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { hasSeenCalendarHint, markCalendarHintShown } from '../../src/utils/onboarding';

export default function CalendarScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const {
    googleEvents,
    isLoading: calLoading,
    selectedDate,
    loadGoogleEvents,
    fetchError: googleFetchError,
  } = useCalendarStore(useShallow((state) => ({
    googleEvents: state.googleEvents,
    isLoading: state.isLoading,
    selectedDate: state.selectedDate,
    loadGoogleEvents: state.loadGoogleEvents,
    fetchError: state.fetchError,
  })));
  const syncFromServer = useRemindersStore((state) => state.syncFromServer);
  const isReminderLoading = useRemindersStore((state) => state.isLoading);
  const refreshProfile = useAuthStore((state) => state.refreshProfile);
  const tabBarBottomSpace = useNavigationStore((state) => state.tabBarBottomSpace);
  const [showCalendarHint, setShowCalendarHint] = useState(false);
  const [quickAddOpenSignal, setQuickAddOpenSignal] = useState(0);
  const [quickAddTargetDate, setQuickAddTargetDate] = useState<string | null>(null);
  const [dragCreateTime, setDragCreateTime] = useState<{ startMinutes: number; durationMinutes: number } | null>(null);
  const [focusRequest, setFocusRequest] = useState<CalendarMonthV2FocusRequest | null>(null);

  const queueFocusRequest = useCallback((dateKey: string, viewMode: CalendarMonthV2FocusRequest['viewMode'] = 'timeline', openDayPanel = true) => {
    setFocusRequest((current) => ({
      id: (current?.id ?? 0) + 1,
      dateKey,
      viewMode,
      openDayPanel,
    }));
  }, []);

  const consumeDeepLinkDate = useCallback(() => {
    const pendingDate = useNavigationStore.getState().consumePendingDeepLinkDate();
    if (!pendingDate) return;
    queueFocusRequest(pendingDate, 'timeline', true);
  }, [queueFocusRequest]);

  const consumeCalendarFocus = useCallback(() => {
    const pendingFocus = useNavigationStore.getState().consumePendingCalendarFocus();
    if (!pendingFocus) return;
    setFocusRequest(pendingFocus);
  }, []);

  useFocusEffect(
    useCallback(() => {
      syncFromServer();
      refreshProfile();
      hasSeenCalendarHint().then((seen) => {
        if (!seen) setShowCalendarHint(true);
      });
      consumeDeepLinkDate();
      consumeCalendarFocus();
    }, [consumeCalendarFocus, consumeDeepLinkDate, refreshProfile, syncFromServer]),
  );

  useEffect(() => {
    loadGoogleEvents();
  }, [loadGoogleEvents]);

  useEffect(() => {
    const unsubscribe = useNavigationStore.subscribe((state, previous) => {
      if (state.pendingDeepLinkDate && state.pendingDeepLinkDate !== previous.pendingDeepLinkDate) {
        consumeDeepLinkDate();
      }
      if (state.pendingCalendarFocus && state.pendingCalendarFocus !== previous.pendingCalendarFocus) {
        consumeCalendarFocus();
      }
    });
    return unsubscribe;
  }, [consumeCalendarFocus, consumeDeepLinkDate]);

  const handleResizeEvent = useCallback(async (seq: number, durationMinutes: number) => {
    try {
      await updateReminder(seq, { durationMinutes });
      await syncFromServer();
    } catch (error) {
      console.warn('[Calendar] resize failed', error);
    }
  }, [syncFromServer]);

  const handleMoveEvent = useCallback(async (seq: number, newRunAtUtc: string) => {
    try {
      await updateReminder(seq, { runAtUtc: newRunAtUtc });
      await syncFromServer();
    } catch (error) {
      console.warn('[Calendar] move failed', error);
    }
  }, [syncFromServer]);

  const handleChangeColumn = useCallback(async (updates: Array<{ seq: number; colPref: number }>) => {
    try {
      await Promise.all(updates.map((update) => updateReminder(update.seq, { colPref: update.colPref })));
      await syncFromServer();
    } catch (error) {
      console.warn('[Calendar] change column failed', error);
    }
  }, [syncFromServer]);

  const handleDragCreate = useCallback((startMinutes: number, durationMinutes: number) => {
    setDragCreateTime({ startMinutes, durationMinutes });
  }, []);

  const handleDragCreateConsumed = useCallback(() => {
    setDragCreateTime(null);
  }, []);

  const handleOpenQuickAdd = useCallback((dateKey?: string) => {
    setQuickAddTargetDate(dateKey ?? null);
    setQuickAddOpenSignal((signal) => signal + 1);
  }, []);

  const handleRefresh = useCallback(() => {
    syncFromServer();
    loadGoogleEvents(true);
  }, [loadGoogleEvents, syncFromServer]);

  const selectedDateKey = selectedDate || DateTime.local().toISODate() || '';
  const quickAddDateKey = quickAddTargetDate ?? selectedDateKey;
  const reservedBottomSpace = tabBarBottomSpace > 0 ? tabBarBottomSpace : 46 + Math.max(insets.bottom, 6);

  return (
    <View style={styles.container}>
      {googleFetchError && googleEvents.length > 0 ? (
        <View style={styles.banner}>
          <Text style={styles.bannerText}>{t('calendar.offlineCached', 'Offline — showing cached events')}</Text>
        </View>
      ) : null}
      <CalendarMonthV2Screen
        additionalCalendarEvents={googleEvents}
        onQuickAdd={handleOpenQuickAdd}
        onResizeEvent={handleResizeEvent}
        onMoveEvent={handleMoveEvent}
        onChangeColumn={handleChangeColumn}
        onDragCreate={handleDragCreate}
        onRefresh={handleRefresh}
        refreshing={calLoading || isReminderLoading}
        showPrototypeNav={false}
        reservedBottomSpace={reservedBottomSpace}
        focusRequest={focusRequest}
      />
      <QuickAddFAB
        selectedDate={quickAddDateKey}
        bottomInset={insets.bottom}
        onCreated={handleRefresh}
        openSignal={quickAddOpenSignal}
        prefillTime={dragCreateTime}
        onPrefillConsumed={handleDragCreateConsumed}
      />
      <CoachMark
        visible={showCalendarHint}
        text={t('onboarding.calendarDragHint')}
        onDismiss={() => {
          markCalendarHintShown();
          setShowCalendarHint(false);
        }}
      />
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  banner: {
    backgroundColor: colors.warning + '33',
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  bannerText: {
    color: colors.text,
    fontSize: 13,
  },
});
